import { useState, useRef, useCallback } from "react";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import * as API from "./api";
import { C } from "./ui/theme";

// Phase 3 (standalone page-file token migration, see linear-brewing-toast.md)
// — this file used to define its own local Fluent/Azure-blue palette here
// (`const T = {...}`, accent:"#0078D4"), completely separate from
// ui/theme.js's Apple palette `C`. Every `C.xxx` reference below has been
// renamed to `C.xxx` (a pure token-name swap, zero value remapping) and the
// local palette object removed.

const REQUIRED_FIELDS=[
  {key:"firstName",label:"First Name",required:true},
  {key:"lastName", label:"Last Name", required:false},
  {key:"email",    label:"Email",     required:true},
  {key:"title",    label:"Title",     required:false},
  {key:"company",  label:"Company",   required:false},
  {key:"linkedin", label:"LinkedIn",  required:false},
];

function Badge({children,bg,tc,sm,onClick,title,style}){
  return <span onClick={onClick} title={title} style={{background:bg,color:tc,fontSize:sm?10:11,padding:sm?"2px 7px":"3px 9px",borderRadius:99,fontWeight:500,whiteSpace:"nowrap",display:"inline-flex",alignItems:"center",...style}}>{children}</span>;
}

// Restyled onto C tokens — was a hardcoded dark-navy border ("#1A1E2E")
// with a blue-purple default color ("#4F6EF7"), neither of which existed
// anywhere else in this file or the app's light Apple palette (same stray
// dark-theme leftover fixed in CampaignsPage.js and App.js's Toast).
function Spinner({size=13,color=C.accent}){
  return <div style={{width:size,height:size,border:`2px solid ${C.border}`,borderTopColor:color,borderRadius:"50%",display:"inline-block",flexShrink:0,animation:"spin .7s linear infinite"}}/>;
}

function Avatar({init,size=32,color=C.accent}){
  return <div style={{width:size,height:size,borderRadius:"50%",background:color+"1A",border:"1px solid "+color+"40",display:"flex",alignItems:"center",justifyContent:"center",fontSize:Math.round(size*.35),fontWeight:500,color,flexShrink:0,fontFamily:"monospace"}}>{init}</div>;
}

export default function ContactsPage({addToast,settings={},setSettings=()=>{}}){
  const toast=addToast||function(){};
  const [contacts,setContacts]=useState([]);
  const [contactFiles,setContactFiles]=useState([]);
  const [loading,setLoading]=useState(false);
  const [importing,setImporting]=useState(false);
  const [step,setStep]=useState("list");
  const [rawData,setRawData]=useState([]);
  const [headers,setHeaders]=useState([]);
  const [mapping,setMapping]=useState({});
  const [preview,setPreview]=useState([]);
  const [importResult,setImportResult]=useState(null);
  const [search,setSearch]=useState("");
  const [selectedFile,setSelectedFile]=useState(null);
  const [fileSearch,setFileSearch]=useState("");
  const [showApollo,setShowApollo]=useState(false);
  const [apolloForm,setApolloForm]=useState({title:"",location:"",keywords:"",companyName:"",industry:"",companySize:"",seniority:"",limit:25});
  const [apolloResults,setApolloResults]=useState([]);
  const [apolloSelected,setApolloSelected]=useState([]);
  const [apolloLoading,setApolloLoading]=useState(false);
  const [apolloImporting,setApolloImporting]=useState(false);
  const [apolloKeyInput,setApolloKeyInput]=useState("");
  const [savingApolloKey,setSavingApolloKey]=useState(false);
  const [dragOver,setDragOver]=useState(false);
  const [fileName,setFileName]=useState("");
  const fileRef=useRef();

  const fetchData=useCallback(async()=>{
    setLoading(true);
    try{
      const token=localStorage.getItem("sdr_token");
      const h={"Authorization":"Bearer "+token};
      const [leadsRes,filesRes]=await Promise.all([
        fetch("/api/leads?limit=10000",{headers:h}),
        fetch("/api/contact-files",{headers:h})
      ]);
      const leads=await leadsRes.json().catch(()=>[]);
      const files=await filesRes.json().catch(()=>[]);
      setContacts(Array.isArray(leads)?leads:[]);
      setContactFiles(Array.isArray(files)?files:[]);
    }catch(e){}
    setLoading(false);
  },[]);

  useState(()=>{fetchData();},[]);

  const autoMap=(hdrs)=>{
    const m={};
    const patterns={
      firstName:["first name","firstname","first_name","given name","fname","first"],
      lastName:["last name","lastname","last_name","surname","lname","last"],
      email:["email","email address","e-mail","mail","work email"],
      title:["title","job title","position","role","jobtitle"],
      company:["company","company name","organization","organisation","employer","account name"],
      linkedin:["linkedin","linkedin url","linkedin_url","profile url","person linkedin"],
    };
    hdrs.forEach(h=>{
      const hl=h.toLowerCase().trim();
      Object.entries(patterns).forEach(([field,pats])=>{
        if(!m[field]&&pats.some(p=>hl.includes(p))) m[field]=h;
      });
    });
    setMapping(m);
  };

  const parseFile=async(file)=>{
    setFileName(file.name);
    const ext=file.name.split(".").pop().toLowerCase();
    if(ext==="csv"){
      Papa.parse(file,{
        header:true,skipEmptyLines:true,
        complete:(result)=>{
          const hdrs=result.meta.fields||[];
          setHeaders(hdrs);setRawData(result.data);
          autoMap(hdrs);setPreview(result.data.slice(0,5));setStep("map");
        },
        error:(e)=>toast("CSV error: "+e.message,"error")
      });
    }else if(["xlsx","xls"].includes(ext)){
      const buf=await file.arrayBuffer();
      const wb=XLSX.read(buf,{type:"array"});
      const ws=wb.Sheets[wb.SheetNames[0]];
      const data=XLSX.utils.sheet_to_json(ws,{defval:""});
      const hdrs=data.length>0?Object.keys(data[0]):[];
      setHeaders(hdrs);setRawData(data);
      autoMap(hdrs);setPreview(data.slice(0,5));setStep("map");
    }else{
      toast("Please upload CSV or Excel file","error");
    }
  };

  const handleDrop=(e)=>{
    e.preventDefault();setDragOver(false);
    const file=e.dataTransfer.files[0];
    if(file)parseFile(file);
  };

  const doImport=async()=>{
    if(!mapping.email){toast("Email field mapping is required","error");return;}
    setImporting(true);
    try{
      const token=localStorage.getItem("sdr_token");
      const res=await fetch("/api/contacts/import",{
        method:"POST",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},
        body:JSON.stringify({contacts:rawData,mapping,fileName})
      });
      const data=await res.json();
      if(!res.ok)throw new Error(data.error||"Import failed");
      setImportResult(data);
      await fetchData();
      setStep("result");
      toast("Imported "+data.imported+" contacts from "+fileName,"success");
    }catch(e){toast("Import failed: "+e.message,"error");}
    setImporting(false);
  };

  const deleteFile=async(fileId)=>{
    try{
      await fetch("/api/contact-files/"+encodeURIComponent(fileId),{method:"DELETE"});
      setContactFiles(p=>p.filter(f=>f.id!==fileId));
      if(selectedFile?.id===fileId)setSelectedFile(null);
      await fetchData();
      toast("File and its contacts deleted","info");
    }catch(e){toast(e.message,"error");}
  };

  const saveApolloKey=async()=>{
    if(!apolloKeyInput.trim()){toast("Paste your Apollo.io API key first","error");return;}
    setSavingApolloKey(true);
    try{
      await API.updateUserSettings({apolloKey:apolloKeyInput.trim()});
      setSettings(p=>({...p,apolloKey:apolloKeyInput.trim()}));
      setApolloKeyInput("");
      toast("Apollo.io connected","success");
    }catch(e){toast("Couldn't save key: "+e.message,"error");}
    setSavingApolloKey(false);
  };

  const searchApollo=async()=>{
    if(!apolloForm.title&&!apolloForm.location&&!apolloForm.keywords&&!apolloForm.companyName){
      toast("Enter at least one filter (title, location, keywords, or company)","error");return;
    }
    setApolloLoading(true);setApolloResults([]);setApolloSelected([]);
    try{
      const res=await API.apolloSearch({...apolloForm,apolloKey:settings.apolloKey});
      if(res.error)throw new Error(res.error);
      if(res.prospects&&res.prospects.length>0){
        setApolloResults(res.prospects);
        toast(`Found ${res.prospects.length} prospects from Apollo`,"success");
      }else{toast("No prospects found — try different filters","info");}
    }catch(e){toast("Apollo search failed: "+e.message,"error");}
    setApolloLoading(false);
  };

  const toggleApolloSelect=(id)=>setApolloSelected(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]);

  const importApolloSelected=async()=>{
    if(!apolloSelected.length){toast("Select at least one prospect","error");return;}
    setApolloImporting(true);
    try{
      const toImport=apolloResults.filter(p=>apolloSelected.includes(p.id));
      const batchName="Apollo Search "+new Date().toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})+" ("+toImport.length+")";
      const token=localStorage.getItem("sdr_token");
      let imported=0,skipped=0;
      for(const p of toImport){
        const res=await fetch('/api/leads',{method:'POST',headers:{'Content-Type':'application/json','Authorization':'Bearer '+token},body:JSON.stringify({...p,contactFile:batchName})});
        if(res.ok)imported++;else skipped++;
      }
      await fetchData();
      setShowApollo(false);setApolloResults([]);setApolloSelected([]);
      toast(`Imported ${imported} contacts into "${batchName}"`+(skipped?` (${skipped} skipped — likely duplicates)`:""),"success");
    }catch(e){toast("Import failed: "+e.message,"error");}
    setApolloImporting(false);
  };

  const exportCSV=(contactsList)=>{
    const rows=[
      ["First Name","Last Name","Title","Company","Email","LinkedIn","Status"],
      ...contactsList.map(c=>[c.firstName,c.lastName,c.title,c.company,c.email,c.linkedin,c.status])
    ];
    const csv=rows.map(r=>r.map(v=>'"'+(v||"")+'"').join(",")).join("\n");
    const blob=new Blob([csv],{type:"text/csv"});
    const url=URL.createObjectURL(blob);
    const a=document.createElement("a");
    a.href=url;a.download=(selectedFile?.name||"contacts")+".csv";a.click();
  };

  const getFileContacts=(file)=>{
    if(!file)return contacts;
    if(file.special==="bounced")return contacts.filter(c=>c.suppressionReason==="hard_bounce"||c.suppressionReason==="soft_bounce");
    if(file.special==="responded")return contacts.filter(c=>c.suppressionReason==="replied");
    if(file.special==="unsubscribed")return contacts.filter(c=>c.suppressionReason==="manual");
    if(file.special==="ungrouped")return contacts.filter(c=>!c.contactFile);
    return contacts.filter(c=>c.contactFile===file.name);
  };

  const bulkUnsuppress=async(list)=>{
    if(!list.length)return;
    if(!window.confirm(`Re-enable ${list.length} contact(s) for future outreach?`))return;
    try{
      const token=localStorage.getItem("sdr_token");
      await Promise.all(list.map(c=>fetch("/api/leads/"+c.id,{method:"PUT",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify({suppressed:false,suppressionReason:null,softBounceCount:0})})));
      await fetchData();
      toast(`${list.length} contact(s) re-enabled`,"success");
    }catch(e){toast("Couldn't update: "+e.message,"error");}
  };

  const displayContacts=selectedFile?getFileContacts(selectedFile):contacts;
  const filtered=displayContacts.filter(c=>
    (c.name||"").toLowerCase().includes(search.toLowerCase())||
    (c.email||"").toLowerCase().includes(search.toLowerCase())||
    (c.company||"").toLowerCase().includes(search.toLowerCase())
  );

  return(
    <div style={{fontFamily:"'Geist',sans-serif",animation:"fadeUp .25s ease both"}}>
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}@keyframes fadeUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}"}</style>

      {/* Header */}
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:22,flexWrap:"wrap",gap:10}}>
        <div>
          {/* Plain professional title — inherits the page's own 'Geist'
              sans-serif; no italic script face, no rainbow gradient fill. */}
          <p style={{fontSize:21,fontWeight:650,letterSpacing:"-.3px",lineHeight:1.25,color:C.text}}>Contacts</p>
          <p style={{fontSize:13,color:C.textSub,marginTop:3}}>
            {contactFiles.length} files  {contacts.length} total contacts
          </p>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <button onClick={()=>exportCSV(filtered)} style={{background:C.bgHover,color:C.textSub,border:"1px solid "+C.border,borderRadius:8,padding:"7px 14px",fontSize:12,cursor:"pointer"}}>
            Export CSV
          </button>
          <button onClick={()=>{if(!settings.apolloKey)window.open("https://developer.apollo.io/keys/","_blank");setShowApollo(true);}} style={{background:"#fff",color:"#5E5FE8",border:"1px solid #5E5FE8",borderRadius:8,padding:"7px 16px",fontSize:12,fontWeight:500,cursor:"pointer"}}>
            Import from Apollo.io
          </button>
          <button onClick={()=>setStep("upload")} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"7px 16px",fontSize:12,fontWeight:500,cursor:"pointer"}}>
            Import Contacts
          </button>
        </div>
      </div>

      {/* Upload step */}
      {step==="upload"&&(
        <div style={{background:C.bgCard,border:"1px solid "+C.border,borderRadius:12,padding:24,marginBottom:16}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
            <p style={{fontSize:14,fontWeight:600,color:C.text}}>Import Contacts</p>
            <button onClick={()=>setStep("list")} style={{background:"none",border:"none",color:C.textSub,cursor:"pointer",fontSize:20}}></button>
          </div>
          <div
            onDrop={handleDrop}
            onDragOver={e=>{e.preventDefault();setDragOver(true);}}
            onDragLeave={()=>setDragOver(false)}
            onClick={()=>fileRef.current.click()}
            style={{border:"2px dashed "+(dragOver?C.accent:C.border),borderRadius:10,padding:"48px 20px",textAlign:"center",cursor:"pointer",background:dragOver?C.accentMuted:"transparent",transition:"all .2s",marginBottom:16}}
          >
            <div style={{fontSize:40,marginBottom:10}}></div>
            <p style={{fontSize:14,fontWeight:500,color:C.text,marginBottom:6}}>Drop your CSV or Excel file here</p>
            <p style={{fontSize:12,color:C.textSub,marginBottom:14}}>Supports .csv, .xlsx, .xls  up to 10,000 contacts</p>
            <button style={{background:C.accent,color:"#fff",border:"none",borderRadius:7,padding:"7px 18px",fontSize:13,cursor:"pointer",fontWeight:500}}>Browse file</button>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={e=>{const f=e.target.files[0];if(f)parseFile(f);}} style={{display:"none"}}/>
          </div>
          <div style={{background:C.bgHover,borderRadius:8,padding:"10px 14px"}}>
            <p style={{fontSize:11,color:C.textSub,textTransform:"uppercase",letterSpacing:".5px",marginBottom:8}}>Expected columns</p>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {["First Name","Last Name","Email","Title","Company","Person LinkedIn"].map(f=>(
                <span key={f} style={{background:C.accentMuted,color:C.accent,fontSize:11,padding:"2px 8px",borderRadius:99}}>{f}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Field mapping step */}
      {step==="map"&&(
        <div style={{background:C.bgCard,border:"1px solid "+C.border,borderRadius:12,padding:24,marginBottom:16}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
            <div>
              <p style={{fontSize:14,fontWeight:600,color:C.text}}>Map Fields</p>
              <p style={{fontSize:12,color:C.textSub}}>File: {fileName}  {rawData.length} rows</p>
            </div>
            <button onClick={()=>setStep("upload")} style={{background:"none",border:"none",color:C.textSub,cursor:"pointer",fontSize:20}}></button>
          </div>
          <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
            {REQUIRED_FIELDS.map(f=>(
              <div key={f.key}>
                <p style={{fontSize:11,color:C.textSub,textTransform:"uppercase",letterSpacing:".5px",marginBottom:5}}>
                  {f.label}{f.required&&<span style={{color:C.red}}> *</span>}
                </p>
                <select value={mapping[f.key]||""} onChange={e=>setMapping(p=>({...p,[f.key]:e.target.value}))}
                  style={{width:"100%",background:C.bgHover,border:"1px solid "+(mapping[f.key]?C.accent:C.border),borderRadius:7,padding:"7px 10px",color:C.text,fontSize:13,outline:"none"}}>
                  <option value="">-- Not mapped --</option>
                  {headers.map(h=><option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            ))}
          </div>
          {preview.length>0&&(
            <div style={{marginBottom:16,overflowX:"auto"}}>
              <p style={{fontSize:11,color:C.textSub,textTransform:"uppercase",letterSpacing:".5px",marginBottom:8}}>Preview  first 5 rows</p>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,border:"1px solid "+C.border,borderRadius:8,overflow:"hidden"}}>
                <thead>
                  <tr style={{background:C.bgHover}}>
                    {REQUIRED_FIELDS.filter(f=>mapping[f.key]).map(f=>(
                      <th key={f.key} style={{padding:"8px 12px",textAlign:"left",color:C.textSub,fontWeight:500,borderBottom:"1px solid "+C.border,whiteSpace:"nowrap"}}>{f.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.map((row,i)=>(
                    <tr key={i} style={{borderBottom:i<preview.length-1?"1px solid "+C.border:"none"}}>
                      {REQUIRED_FIELDS.filter(f=>mapping[f.key]).map(f=>(
                        <td key={f.key} style={{padding:"8px 12px",color:C.text}}>{String(row[mapping[f.key]]||"")}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{display:"flex",gap:8}}>
            <button onClick={doImport} disabled={importing||!mapping.email}
              style={{background:importing||!mapping.email?C.accentMuted:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"8px 20px",fontSize:13,fontWeight:500,cursor:importing||!mapping.email?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:6}}>
              {importing?(<><Spinner size={12} color="#fff"/> Importing...</>):("Import "+rawData.length+" contacts")}
            </button>
            <button onClick={()=>setStep("upload")} style={{background:"transparent",color:C.textSub,border:"1px solid "+C.border,borderRadius:8,padding:"8px 16px",fontSize:13,cursor:"pointer"}}>Back</button>
          </div>
        </div>
      )}

      {/* Import result */}
      {step==="result"&&importResult&&(
        <div style={{background:C.bgCard,border:"1px solid "+C.border,borderRadius:12,padding:24,marginBottom:16}}>
          <p style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:16}}>Import Complete  {fileName}</p>
          <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:16}}>
            <div style={{background:C.greenMuted,borderRadius:8,padding:"14px 16px",textAlign:"center"}}>
              <p style={{fontSize:28,fontWeight:700,color:C.green,fontFamily:"monospace",lineHeight:1}}>{importResult.imported}</p>
              <p style={{fontSize:12,color:C.green,marginTop:6}}>Imported</p>
            </div>
            <div style={{background:C.redMuted,borderRadius:8,padding:"14px 16px",textAlign:"center"}}>
              <p style={{fontSize:28,fontWeight:700,color:C.red,fontFamily:"monospace",lineHeight:1}}>{importResult.errors}</p>
              <p style={{fontSize:12,color:C.red,marginTop:6}}>Errors</p>
            </div>
            <div style={{background:C.accentMuted,borderRadius:8,padding:"14px 16px",textAlign:"center"}}>
              <p style={{fontSize:28,fontWeight:700,color:C.accent,fontFamily:"monospace",lineHeight:1}}>{importResult.total}</p>
              <p style={{fontSize:12,color:C.accent,marginTop:6}}>Total rows</p>
            </div>
          </div>
          {importResult.errorDetails&&importResult.errorDetails.length>0&&(
            <div style={{background:C.redMuted,border:"1px solid "+C.red+"33",borderRadius:8,padding:"10px 14px",marginBottom:14}}>
              <p style={{fontSize:12,color:C.red,marginBottom:6,fontWeight:500}}>Errors (first 10):</p>
              {importResult.errorDetails.map((e,i)=>(
                <p key={i} style={{fontSize:11,color:C.red,marginBottom:2}}>Row {e.row}: {e.reason}</p>
              ))}
            </div>
          )}
          <button onClick={()=>setStep("list")} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"8px 20px",fontSize:13,fontWeight:500,cursor:"pointer"}}>
            View contacts
          </button>
        </div>
      )}

      {/* List view */}
      {step==="list"&&(
        <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"260px 1fr",gap:16}}>

          {/* File sidebar */}
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            <div style={{background:C.bgCard,border:"1px solid "+C.border,borderRadius:10,padding:"10px 12px"}}>
              <p style={{fontSize:11,color:C.textSub,textTransform:"uppercase",letterSpacing:".5px",marginBottom:8}}>Contact Files</p>

              {/* All contacts option */}
              <button onClick={()=>setSelectedFile(null)}
                style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 10px",borderRadius:7,border:"1px solid "+(selectedFile===null?C.accent:C.border),background:selectedFile===null?C.accentMuted:"transparent",cursor:"pointer",marginBottom:6}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:16}}></span>
                  <span style={{fontSize:13,color:selectedFile===null?C.accent:C.text,fontWeight:selectedFile===null?500:400}}>All Contacts</span>
                </div>
                <span style={{fontSize:11,color:C.textSub,background:C.bgHover,padding:"2px 7px",borderRadius:99}}>{contacts.length}</span>
              </button>

              {/* Bounced / Responded / Unsubscribed — for reviewing and reworking suppressed contacts */}
              {(()=>{
                const bouncedCount=contacts.filter(c=>c.suppressionReason==="hard_bounce"||c.suppressionReason==="soft_bounce").length;
                const respondedCount=contacts.filter(c=>c.suppressionReason==="replied").length;
                const unsubscribedCount=contacts.filter(c=>c.suppressionReason==="manual").length;
                const ungroupedCount=contacts.filter(c=>!c.contactFile).length;
                return(
                  <>
                    <button onClick={()=>setSelectedFile({id:"__bounced__",name:"__bounced__",special:"bounced"})}
                      style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 10px",borderRadius:7,border:"1px solid "+(selectedFile?.special==="bounced"?C.red:C.border),background:selectedFile?.special==="bounced"?C.redMuted:"transparent",cursor:"pointer",marginBottom:6}}>
                      <span style={{fontSize:13,color:selectedFile?.special==="bounced"?C.red:C.text,fontWeight:selectedFile?.special==="bounced"?500:400}}>Bounced (rework)</span>
                      <span style={{fontSize:11,color:C.red,background:C.redMuted,padding:"2px 7px",borderRadius:99}}>{bouncedCount}</span>
                    </button>
                    <button onClick={()=>setSelectedFile({id:"__responded__",name:"__responded__",special:"responded"})}
                      style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 10px",borderRadius:7,border:"1px solid "+(selectedFile?.special==="responded"?C.green:C.border),background:selectedFile?.special==="responded"?C.greenMuted:"transparent",cursor:"pointer",marginBottom:6}}>
                      <span style={{fontSize:13,color:selectedFile?.special==="responded"?C.green:C.text,fontWeight:selectedFile?.special==="responded"?500:400}}>Responded</span>
                      <span style={{fontSize:11,color:C.green,background:C.greenMuted,padding:"2px 7px",borderRadius:99}}>{respondedCount}</span>
                    </button>
                    <button onClick={()=>setSelectedFile({id:"__unsubscribed__",name:"__unsubscribed__",special:"unsubscribed"})}
                      style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 10px",borderRadius:7,border:"1px solid "+(selectedFile?.special==="unsubscribed"?C.amber:C.border),background:selectedFile?.special==="unsubscribed"?C.amberMuted:"transparent",cursor:"pointer",marginBottom:6}}
                      title="Contacts who clicked the unsubscribe link in one of your emails">
                      <span style={{fontSize:13,color:selectedFile?.special==="unsubscribed"?C.amber:C.text,fontWeight:selectedFile?.special==="unsubscribed"?500:400}}>Unsubscribed</span>
                      <span style={{fontSize:11,color:C.amber,background:C.amberMuted,padding:"2px 7px",borderRadius:99}}>{unsubscribedCount}</span>
                    </button>
                    {ungroupedCount>0&&(
                      <button onClick={()=>setSelectedFile({id:"__ungrouped__",name:"__ungrouped__",special:"ungrouped"})}
                        style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 10px",borderRadius:7,border:"1px solid "+(selectedFile?.special==="ungrouped"?C.accent:C.border),background:selectedFile?.special==="ungrouped"?C.accentMuted:"transparent",cursor:"pointer",marginBottom:6}}
                        title="Contacts not tied to any uploaded file — e.g. added via Discover/Apollo search, or one at a time">
                        <span style={{fontSize:13,color:selectedFile?.special==="ungrouped"?C.accent:C.text,fontWeight:selectedFile?.special==="ungrouped"?500:400}}>Ungrouped</span>
                        <span style={{fontSize:11,color:C.textSub,background:C.bgHover,padding:"2px 7px",borderRadius:99}}>{ungroupedCount}</span>
                      </button>
                    )}
                  </>
                );
              })()}

              {/* Individual files */}
              {contactFiles.length>0&&(
                <select value={selectedFile&&!selectedFile.special?selectedFile.id:""} onChange={e=>{
                    const f=contactFiles.find(cf=>cf.id===e.target.value);
                    setSelectedFile(f||null);
                  }}
                  style={{width:"100%",border:"1px solid "+C.border,borderRadius:7,padding:"7px 10px",fontSize:12,outline:"none",marginBottom:8,background:"#fff",color:C.text}}>
                  <option value="">-- Select contact file... ({contactFiles.length}) --</option>
                  {contactFiles.map(f=>(
                    <option key={f.id} value={f.id}>{f.name} ({f.count} contacts)</option>
                  ))}
                </select>
              )}
              {contactFiles.length>3&&(
                <input value={fileSearch} onChange={e=>setFileSearch(e.target.value)} placeholder="Search files by name..."
                  style={{width:"100%",border:"1px solid "+C.border,borderRadius:7,padding:"6px 10px",fontSize:12,outline:"none",marginBottom:6,boxSizing:"border-box"}}/>
              )}
              {contactFiles.length===0&&(
                <p style={{fontSize:12,color:C.textMuted,padding:"8px 4px"}}>No files imported yet</p>
              )}
              {(()=>{
                const q=fileSearch.toLowerCase();
                const visibleFiles=q?contactFiles.filter(f=>(f.name||"").toLowerCase().includes(q)):contactFiles;
                if(contactFiles.length>0&&visibleFiles.length===0)return<p style={{fontSize:12,color:C.textMuted,padding:"8px 4px"}}>No files match "{fileSearch}"</p>;
                return visibleFiles.map(file=>(
                <div key={file.id} style={{marginBottom:4}}>
                  <button onClick={()=>setSelectedFile(file)}
                    style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 10px",borderRadius:7,border:"1px solid "+(selectedFile?.id===file.id?C.accent:C.border),background:selectedFile?.id===file.id?C.accentMuted:"transparent",cursor:"pointer"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,flex:1,minWidth:0}}>
                      <span style={{fontSize:14}}></span>
                      <div style={{flex:1,minWidth:0,textAlign:"left"}}>
                        <p style={{fontSize:12,color:selectedFile?.id===file.id?C.accent:C.text,fontWeight:selectedFile?.id===file.id?500:400,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{file.name}</p>
                        <p style={{fontSize:10,color:C.textSub}}>{new Date(file.importedAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <span style={{fontSize:11,color:C.textSub,background:C.bgHover,padding:"2px 7px",borderRadius:99,flexShrink:0,marginLeft:4}}>{file.count}</span>
                  </button>
                  {selectedFile?.id===file.id&&(
                    <button onClick={()=>deleteFile(file.id)}
                      style={{width:"100%",marginTop:3,background:C.redMuted,color:C.red,border:"none",borderRadius:6,padding:"4px 10px",fontSize:11,cursor:"pointer"}}>
                      Delete this file
                    </button>
                  )}
                </div>
              ));
              })()}

              <button onClick={()=>setStep("upload")}
                style={{width:"100%",marginTop:8,background:C.accent,color:"#fff",border:"none",borderRadius:7,padding:"7px 12px",fontSize:12,fontWeight:500,cursor:"pointer"}}>
                + Import new file
              </button>
            </div>

            {selectedFile&&selectedFile.special&&(()=>{
              const list=getFileContacts(selectedFile);
              const isBounced=selectedFile.special==="bounced";
              const isUngrouped=selectedFile.special==="ungrouped";
              const isUnsubscribed=selectedFile.special==="unsubscribed";
              const color=isBounced?C.red:isUngrouped?C.accent:isUnsubscribed?C.amber:C.green;
              const bg=isBounced?C.redMuted:isUngrouped?C.accentMuted:isUnsubscribed?C.amberMuted:C.greenMuted;
              const label=isBounced?"Bounced contacts":isUngrouped?"Ungrouped contacts":isUnsubscribed?"Unsubscribed contacts":"Responded contacts";
              const note=isBounced?" — fix their email or details, then re-enable to include them again":isUngrouped?" — added via Discover/Apollo search or one at a time, not tied to any uploaded file":isUnsubscribed?" — clicked the unsubscribe link in an email; re-enabling overrides their opt-out, so only do this if they've asked to be added back":" — automatically excluded from future sequences/campaigns";
              return(
                <div style={{background:bg,border:"1px solid "+color+"33",borderRadius:10,padding:"10px 12px"}}>
                  <p style={{fontSize:11,color,textTransform:"uppercase",letterSpacing:".5px",marginBottom:4}}>{label}</p>
                  <p style={{fontSize:12,color,opacity:.8}}>{list.length} contacts{note}</p>
                  {list.length>0&&!isUngrouped&&(
                    <button onClick={()=>bulkUnsuppress(list)} style={{marginTop:8,background:color,color:"#fff",border:"none",borderRadius:6,padding:"6px 12px",fontSize:11,cursor:"pointer"}}>
                      Re-enable all {list.length}
                    </button>
                  )}
                </div>
              );
            })()}
            {selectedFile&&!selectedFile.special&&(
              <div style={{background:C.greenMuted,border:"1px solid "+C.green+"33",borderRadius:10,padding:"10px 12px"}}>
                <p style={{fontSize:11,color:C.green,textTransform:"uppercase",letterSpacing:".5px",marginBottom:4}}>Selected file</p>
                <p style={{fontSize:13,fontWeight:500,color:C.green}}>{selectedFile.name}</p>
                <p style={{fontSize:12,color:C.green,opacity:.8}}>{selectedFile.count} contacts</p>
                <p style={{fontSize:11,color:C.green,opacity:.7,marginTop:4}}>This file is available in Sequence Builder</p>
              </div>
            )}
          </div>

          {/* Contacts table */}
          <div>
            <div style={{display:"flex",gap:8,marginBottom:12}}>
              <input value={search} onChange={e=>setSearch(e.target.value)}
                placeholder="Search contacts..."
                style={{flex:1,background:C.bgCard,border:"1px solid "+C.border,borderRadius:8,padding:"8px 12px",color:C.text,fontSize:13,outline:"none"}}/>
              <button onClick={fetchData} style={{background:C.bgHover,color:C.textSub,border:"1px solid "+C.border,borderRadius:8,padding:"8px 14px",fontSize:12,cursor:"pointer"}}>Refresh</button>
              <button onClick={()=>exportCSV(filtered)} style={{background:C.bgHover,color:C.textSub,border:"1px solid "+C.border,borderRadius:8,padding:"8px 14px",fontSize:12,cursor:"pointer"}}>Export</button>
            </div>

            <div style={{background:C.bgCard,border:"1px solid "+C.border,borderRadius:12,overflow:"hidden"}}>
              <div className="hide-on-mobile" style={{display:"grid",gridTemplateColumns:"1.1fr 1fr 1.1fr 1.4fr 1.7fr 90px",gap:8,padding:"9px 16px",background:C.bgHover,borderBottom:"1px solid "+C.border}}>
                {["First Name","Last Name","Title","Company","Email","Status"].map((h,i)=>(
                  <span key={i} style={{fontSize:10,color:C.textSub,textTransform:"uppercase",letterSpacing:".6px",fontWeight:500}}>{h}</span>
                ))}
              </div>

              {loading&&(
                <div style={{padding:40,textAlign:"center",color:C.textSub,display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                  <Spinner/> Loading...
                </div>
              )}

              {!loading&&filtered.length===0&&(
                <div style={{padding:"40px 20px",textAlign:"center"}}>
                  <div style={{fontSize:36,marginBottom:8}}></div>
                  <p style={{fontSize:14,color:C.textSub,marginBottom:4}}>
                    {selectedFile?"No contacts in this file":"No contacts yet"}
                  </p>
                  <button onClick={()=>setStep("upload")} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"8px 20px",fontSize:13,cursor:"pointer",fontWeight:500,marginTop:12}}>
                    Import contacts
                  </button>
                </div>
              )}

              {filtered.slice(0,200).map((c,i)=>{
                const firstInit=((c.firstName||c.name||"?")[0]||"?").toUpperCase();
                const lastInit=((c.lastName||"?")[0]||"?").toUpperCase();
                return(
                  <div key={c.id} className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1.1fr 1fr 1.1fr 1.4fr 1.7fr 90px",gap:8,padding:"9px 16px",borderBottom:i<Math.min(filtered.length,200)-1?"1px solid "+C.border:"none",alignItems:"center",transition:"background .1s"}}
                    onMouseEnter={e=>e.currentTarget.style.background=C.bgHover}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0}}>
                      <Avatar init={(firstInit+lastInit).slice(0,2)} size={26} color={C.accent}/>
                      <div style={{minWidth:0}}>
                        <p style={{fontSize:13,fontWeight:500,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.firstName||c.name||c.email}</p>
                        {c.linkedin&&<a href={c.linkedin} target="_blank" rel="noreferrer" style={{fontSize:10,color:C.accent,textDecoration:"none"}}>LinkedIn</a>}
                      </div>
                    </div>
                    <span style={{fontSize:13,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.lastName||""}</span>
                    <span style={{fontSize:12,color:C.textSub,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.title||""}</span>
                    <span style={{fontSize:12,color:C.textSub,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.company||""}</span>
                    <span style={{fontSize:11,color:C.textSub,fontFamily:"monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.email||""}</span>
                    {c.suppressed?(
                      <Badge bg={C.redMuted||"#FDE7E9"} tc={C.red||"#A4262C"} sm onClick={async()=>{
                        try{
                          const token=localStorage.getItem("sdr_token");
                          await fetch("/api/leads/"+c.id,{method:"PUT",headers:{"Content-Type":"application/json","Authorization":"Bearer "+token},body:JSON.stringify({suppressed:false,suppressionReason:null})});
                          await fetchData();
                          toast("Contact re-enabled","success");
                        }catch(e){toast("Couldn't update: "+e.message,"error");}
                      }} style={{cursor:"pointer"}} title={"Suppressed: "+(c.suppressionReason||"unknown")+" — click to re-enable"}>
                        {c.suppressionReason==="replied"?"Replied":c.suppressionReason==="complained"?"Complained":c.suppressionReason==="hard_bounce"||c.suppressionReason==="soft_bounce"?"Bounced":c.suppressionReason==="manual"?"Unsubscribed":"Suppressed"}
                      </Badge>
                    ):(
                      <Badge bg={C.accentMuted} tc={C.accent} sm>{c.status||"New"}</Badge>
                    )}
                  </div>
                );
              })}
            </div>

            <p style={{fontSize:12,color:C.textSub,marginTop:8,textAlign:"right"}}>
              Showing {Math.min(filtered.length,200)} of {filtered.length} contacts
              {selectedFile?" in "+selectedFile.name:""}
            </p>
          </div>
        </div>
      )}

      {showApollo&&(
        <div onClick={()=>setShowApollo(false)} style={{position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:99999,background:"rgba(0,0,0,.5)"}}>
          <div onClick={e=>e.stopPropagation()} style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",background:"#ffffff",borderRadius:12,padding:24,width:600,maxWidth:"90vw",maxHeight:"85vh",overflowY:"auto",boxShadow:"0 10px 40px rgba(0,0,0,.3)"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <p style={{fontSize:16,fontWeight:600,color:C.text}}>Import from Apollo.io</p>
              <button onClick={()=>setShowApollo(false)} style={{background:"none",border:"none",color:C.textSub,cursor:"pointer",fontSize:20}}>&times;</button>
            </div>

            {!settings.apolloKey?(
              <div>
                <p style={{fontSize:13,color:C.textSub,marginBottom:12,lineHeight:1.6}}>
                  Connect your own Apollo.io account to search and import contacts. This uses your personal Apollo API key — your searches and credits stay on your own Apollo account, separate from any other user.
                </p>
                <a href="https://developer.apollo.io/keys/" target="_blank" rel="noreferrer"
                  style={{display:"inline-block",background:"#5E5FE8",color:"#fff",borderRadius:8,padding:"9px 18px",fontSize:13,fontWeight:500,textDecoration:"none",marginBottom:16}}>
                  Open Apollo.io to get your API key ↗
                </a>
                <p style={{fontSize:11,color:C.textSub,marginBottom:16}}>Log into your own Apollo.io account, go to Settings → Integrations → API, copy your key, then paste it below.</p>
                <label style={{fontSize:11,fontWeight:600,color:C.textSub,textTransform:"uppercase",letterSpacing:".4px",display:"block",marginBottom:4}}>Your Apollo.io API Key</label>
                <input type="password" value={apolloKeyInput} onChange={e=>setApolloKeyInput(e.target.value)} placeholder="Paste your API key here"
                  style={{width:"100%",border:"1px solid "+C.border,borderRadius:8,padding:"9px 12px",fontSize:13,outline:"none",boxSizing:"border-box",marginBottom:10}}/>
                <button onClick={saveApolloKey} disabled={savingApolloKey} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"8px 18px",fontSize:13,fontWeight:600,cursor:"pointer"}}>
                  {savingApolloKey?"Connecting...":"Connect Apollo.io"}
                </button>
              </div>
            ):(
              <div>
                <div className="responsive-grid" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                  <input value={apolloForm.title} onChange={e=>setApolloForm(p=>({...p,title:e.target.value}))} placeholder="Job title (e.g. VP Sales)" style={{border:"1px solid "+C.border,borderRadius:7,padding:"8px 10px",fontSize:12,outline:"none"}}/>
                  <input value={apolloForm.location} onChange={e=>setApolloForm(p=>({...p,location:e.target.value}))} placeholder="Location (e.g. San Francisco)" style={{border:"1px solid "+C.border,borderRadius:7,padding:"8px 10px",fontSize:12,outline:"none"}}/>
                  <input value={apolloForm.companyName} onChange={e=>setApolloForm(p=>({...p,companyName:e.target.value}))} placeholder="Company name" style={{border:"1px solid "+C.border,borderRadius:7,padding:"8px 10px",fontSize:12,outline:"none"}}/>
                  <input value={apolloForm.keywords} onChange={e=>setApolloForm(p=>({...p,keywords:e.target.value}))} placeholder="Keywords" style={{border:"1px solid "+C.border,borderRadius:7,padding:"8px 10px",fontSize:12,outline:"none"}}/>
                  <select value={apolloForm.seniority} onChange={e=>setApolloForm(p=>({...p,seniority:e.target.value}))} style={{border:"1px solid "+C.border,borderRadius:7,padding:"8px 10px",fontSize:12,outline:"none"}}>
                    <option value="">Any seniority</option>
                    <option value="owner">Owner</option><option value="founder">Founder</option><option value="c_suite">C-Suite</option>
                    <option value="vp">VP</option><option value="director">Director</option><option value="manager">Manager</option>
                  </select>
                  <select value={apolloForm.companySize} onChange={e=>setApolloForm(p=>({...p,companySize:e.target.value}))} style={{border:"1px solid "+C.border,borderRadius:7,padding:"8px 10px",fontSize:12,outline:"none"}}>
                    <option value="">Any company size</option>
                    {["1-10","11-50","51-200","201-500","501-1000","1001-5000","5000+"].map(s=><option key={s} value={s}>{s} employees</option>)}
                  </select>
                </div>
                <button onClick={searchApollo} disabled={apolloLoading} style={{background:"#5E5FE8",color:"#fff",border:"none",borderRadius:8,padding:"8px 18px",fontSize:13,fontWeight:600,cursor:"pointer",marginBottom:16}}>
                  {apolloLoading?"Searching...":"Search Apollo.io"}
                </button>

                {apolloResults.length>0&&(
                  <>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                      <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:C.textSub,cursor:"pointer"}}>
                        <input type="checkbox" checked={apolloSelected.length===apolloResults.length} onChange={()=>setApolloSelected(apolloSelected.length===apolloResults.length?[]:apolloResults.map(p=>p.id))} style={{width:"auto"}}/>
                        Select all ({apolloResults.length})
                      </label>
                      <span style={{fontSize:12,color:C.textSub}}>{apolloSelected.length} selected</span>
                    </div>
                    <div style={{maxHeight:220,overflowY:"auto",border:"1px solid "+C.border,borderRadius:8,marginBottom:12}}>
                      {apolloResults.map(p=>(
                        <label key={p.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderBottom:"1px solid "+C.border,cursor:"pointer",fontSize:12}}>
                          <input type="checkbox" checked={apolloSelected.includes(p.id)} onChange={()=>toggleApolloSelect(p.id)} style={{width:"auto"}}/>
                          <span style={{flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{p.name||p.email} {p.company?<span style={{color:C.textSub}}>— {p.company}</span>:null}</span>
                          <span style={{fontSize:10,color:C.textMuted,fontFamily:"monospace"}}>{p.email}</span>
                        </label>
                      ))}
                    </div>
                    <button onClick={importApolloSelected} disabled={apolloImporting||!apolloSelected.length} style={{background:C.accent,color:"#fff",border:"none",borderRadius:8,padding:"8px 18px",fontSize:13,fontWeight:600,cursor:"pointer"}}>
                      {apolloImporting?"Importing...":`Import ${apolloSelected.length} Selected Contacts`}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}